/* eslint-disable indent */
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Card,
  Button,
  Spinner,
  Form,
  Table,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import { Link } from 'react-router-dom'
import loginUser from '../services/login'
import AddPlayersByIdModal from './AddPlayersByIdModal'
import CandidateCsvSyncModal from './CandidateCsvSyncModal'
import CopyCandidateListModal from './CopyCandidateListModal'
import RemovePlayersPaintModal from './RemovePlayersPaintModal'
import TrainingStandardsModal from './TrainingStandardsModal'
import {
  specArray,
  taidotLyh,
  skills as modeToSkillMap,
} from '../services/arrays'
import {
  isOld,
  isOverMonthOld,
  calculateActualAge,
  calculateEstimatedForm,
  dateFin,
  BackToListsButton,
} from '../services/helpfunctions'
import { getInjuryBadge } from '../services/injury'
import {
  initializeCandidates,
  fetchCandidateDetail,
  addCandidate,
  setCandidateDetail,
  updateCandidatePlayerCount,
  updateCandidateSummary,
  setCandidateListViewState,
} from '../reducers/candidateReducer'
import { initializeBundles } from '../reducers/bundleReducer'
import { changeUser } from '../reducers/userReducer'
import {
  getTournamentFromCandidate,
  getU21TournamentEligibility,
} from '../services/u21utils'
import {
  calculatePositionHtms,
  getCandidatePosition,
  getPositionHtmsSkills,
} from '../services/positionHtms'

const TEAM_TYPE_FILTER_STORAGE_KEY = 'candidateTeamTypeFilter'

const DEFAULT_HIDDEN_COLUMNS_BY_POSITION = {
  DEFAULT: ['trainingPlan'],
  FW_PNF_TDF: ['keeperSkill', 'defenderSkill', 'trainingPlan'],
  IM: ['keeperSkill', 'trainingPlan'],
  CA_CD: ['keeperSkill', 'wingerSkill', 'scorerSkill', 'trainingPlan'],
  WB_ETWB: ['keeperSkill', 'scorerSkill', 'trainingPlan'],
  GK: ['playmakerSkill', 'wingerSkill', 'scorerSkill', 'trainingPlan'],
}

const MANAGEABLE_COLUMNS = [
  { key: 'name', label: 'Nimi' },
  { key: 'playerId', label: 'ID' },
  { key: 'age', label: 'Ikä' },
  { key: 'tournament', label: 'Turnaus' },
  { key: 'specialty', label: 'Erik.' },
  { key: 'playerForm', label: 'KU' },
  { key: 'staminaSkill', label: 'Kest. taito' },
  { key: 'keeperSkill', label: 'MV' },
  { key: 'defenderSkill', label: 'PU' },
  { key: 'playmakerSkill', label: 'PR' },
  { key: 'wingerSkill', label: 'LA' },
  { key: 'passingSkill', label: 'SY' },
  { key: 'scorerSkill', label: 'MT' },
  { key: 'setPiecesSkill', label: 'ET' },
  { key: 'experience', label: 'Kok' },
  { key: 'leadership', label: 'Joht.' },
  { key: 'tsi', label: 'KS (Kunnon suunta)' },
  { key: 'htms', label: 'HTMS' },
  { key: 'positionHtms', label: 'HTMS(pelipaikka)' },
  { key: 'potHtms', label: 'Pot28' },
  { key: 'coach', label: 'V' },
  { key: 'assistants', label: 'AV' },
  { key: 'intensity', label: 'Int.' },
  { key: 'staminaTraining', label: 'Kest. %' },
  { key: 'minutes', label: 'Mins' },
  { key: 'efficiency', label: 'Ø' },
  { key: 'medic', label: 'L' },
  { key: 'formCoach', label: 'KV' },
  { key: 'trainingMode', label: 'Harj.' },
  { key: 'trainingPlan', label: 'Plan' },
  { key: 'owner', label: 'Omistaja' },
  { key: 'manageVisibility', label: 'Piilota' },
  { key: 'manageDelete', label: 'Poista' },
]

const SPECIALTY_FILTER_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 8]

const normalizePositionKey = position => {
  const value = String(position || '').toUpperCase()

  if (/\b(FW|PNF|TDF)\b/.test(value)) return 'FW_PNF_TDF'
  if (/\bIM\b/.test(value)) return 'IM'
  if (/\b(CA|CD)\b/.test(value)) return 'CA_CD'
  if (/\b(WB|ETWB)\b/.test(value)) return 'WB_ETWB'
  if (/\bGK\b/.test(value)) return 'GK'

  return 'DEFAULT'
}

const getInitialTeamTypeFilter = () => {
  try {
    const saved = window.localStorage.getItem(TEAM_TYPE_FILTER_STORAGE_KEY)
    return saved === 'MJ' || saved === 'U21' ? saved : 'U21'
  } catch {
    return 'U21'
  }
}

const getValidPlayerEntries = entries =>
  (entries || []).filter(entry => entry && entry.player)

const getSpecialtyIconPath = specialty => {
  const value = Number(specialty)
  if (Number.isFinite(value)) {
    return `/${value}.png`
  }
  return '/0.png'
}

const formatStaminaSkill = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed.toFixed(1) : '-'
}

const formatPlayerForm = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : '-'
}

const getFormAndStaminaColor = value => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return null

  if (parsed <= 3) return '#c0392b'
  if (parsed <= 5) return '#e67e22'
  if (parsed < 7) return '#f1c40f'
  if (parsed < 8) return '#7dff9b'
  return '#2ecc71'
}

const hasNumericValue = value =>
  value !== null &&
  value !== undefined &&
  value !== '' &&
  Number.isFinite(Number(value))

const getEstimatedFormDirection = player => {
  const currentEstimatedForm = calculateEstimatedForm(
    player,
    player?.playerSkills
  )
  const history = Array.isArray(player?.trainhistory)
    ? player.trainhistory.filter(
        entry =>
          hasNumericValue(entry?.tsi) &&
          entry?.playerSkills &&
          typeof entry.playerSkills === 'object'
      )
    : []
  const latestHistoryEntry = history[history.length - 1]
  const previousHistoryEntry =
    Number(latestHistoryEntry?.tsi) === Number(player?.tsi)
      ? history[history.length - 2]
      : latestHistoryEntry
  const previousEstimatedForm = previousHistoryEntry
    ? calculateEstimatedForm(
        { ...player, tsi: previousHistoryEntry.tsi },
        previousHistoryEntry.playerSkills
      )
    : null

  if (
    !Number.isFinite(currentEstimatedForm) ||
    !Number.isFinite(previousEstimatedForm)
  ) {
    return null
  }

  if (currentEstimatedForm > previousEstimatedForm) {
    return { indicator: '\u2191', color: '#2ecc71' }
  }
  if (currentEstimatedForm < previousEstimatedForm) {
    return { indicator: '\u2193', color: '#e74c3c' }
  }
  return null
}

const EnhancedCandidatesList = () => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(state => state.user)
  const savedViewState = useSelector(
    state => state.candidates.listViewStateById[id] || null
  )

  // State
  const [candidate, setCandidate] = useState(null)
  const [allCandidates, setAllCandidates] = useState([])
  const [players, setPlayers] = useState([])
  const [isLoadingHeader, setIsLoadingHeader] = useState(true)
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false)
  const [error, setError] = useState('')

  // Niputus: kaikki niputetut listat ladattuna
  const [bundledCandidates, setBundledCandidates] = useState([]) // [{candidate, players, trainingStandards}, ...]
  const [activeBundleListId, setActiveBundleListId] = useState(null) // Minkä listan modaali on auki

  // Harjoitussuunnitelman tarkistukset
  const [trainingChecks, setTrainingChecks] = useState({})

  // Modaalit
  const [showAddModal, setShowAddModal] = useState(false)
  const [showCsvSyncModal, setShowCsvSyncModal] = useState(false)
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [showRemovePaintModal, setShowRemovePaintModal] = useState(false)
  const [showStandardsModal, setShowStandardsModal] = useState(false)
  const [copySourceListId, setCopySourceListId] = useState(null)
  const [removeSourceListId, setRemoveSourceListId] = useState(null)

  // Harjoitusvaatimukset
  const [trainingStandards, setTrainingStandards] = useState(null)

  // Listasuodattimet
  const [teamTypeFilter, setTeamTypeFilter] = useState(
    savedViewState?.teamTypeFilter || getInitialTeamTypeFilter()
  ) // 'MJ' tai 'U21'
  const [seasonFilter, setSeasonFilter] = useState(
    savedViewState?.seasonFilter ?? null
  ) // Kausi U21-listoille
  const [filtersInitialized, setFiltersInitialized] = useState(
    savedViewState?.filtersInitialized || false
  )

  // Sorttaus
  const [sortBy, setSortBy] = useState(savedViewState?.sortBy || 'htms')
  const [sortOrder, setSortOrder] = useState(
    savedViewState?.sortOrder || 'desc'
  )
  const [secondarySort, setSecondarySort] = useState(
    savedViewState?.secondarySort ?? null
  )
  const [filterOnlyInvalid, setFilterOnlyInvalid] = useState(
    savedViewState?.filterOnlyInvalid || false
  )
  const [manageMode, setManageMode] = useState(
    savedViewState?.manageMode || false
  )
  const [confirmDelete, setConfirmDelete] = useState(null) // playerId jota ollaan poistamassa
  const [hiddenColumnsByPosition, setHiddenColumnsByPosition] = useState(
    savedViewState?.hiddenColumnsByPosition || {}
  )
  const [minAgeYears, setMinAgeYears] = useState(
    savedViewState?.minAgeYears ?? ''
  )
  const [maxAgeYears, setMaxAgeYears] = useState(
    savedViewState?.maxAgeYears ?? ''
  )
  const [selectedSpecialties, setSelectedSpecialties] = useState(
    savedViewState?.selectedSpecialties || []
  )

  // Hae kaikki ehdokaslistat Reduxista (tai backendistä jos ei cachessa)
  const cachedCandidates = useSelector(state => state.candidates.allCandidates)
  const candidatesLoaded = useSelector(
    state => state.candidates.allCandidatesLoaded
  )
  const bundles = useSelector(state => state.bundles.allBundles)
  const favoriteCandidateIds = new Set(
    (user?.favoriteCandidateLists || []).map(favoriteId => String(favoriteId))
  )

  // Etsi nykyisen listan nippu
  const currentBundle = bundles.find(b =>
    b.candidates.some(c => (c.id || c._id || c) === id)
  )
  const isBundled = !!currentBundle

  // Palauta listakohtainen näkymätila kun URL-lista vaihtuu
  useEffect(() => {
    if (!id) return

    if (savedViewState) {
      setTeamTypeFilter(
        savedViewState.teamTypeFilter || getInitialTeamTypeFilter()
      )
      setSeasonFilter(savedViewState.seasonFilter ?? null)
      setFiltersInitialized(savedViewState.filtersInitialized || false)
      setSortBy(savedViewState.sortBy || 'htms')
      setSortOrder(savedViewState.sortOrder || 'desc')
      setSecondarySort(savedViewState.secondarySort ?? null)
      setFilterOnlyInvalid(savedViewState.filterOnlyInvalid || false)
      setManageMode(savedViewState.manageMode || false)
      setHiddenColumnsByPosition(savedViewState.hiddenColumnsByPosition || {})
      setMinAgeYears(savedViewState.minAgeYears ?? '')
      setMaxAgeYears(savedViewState.maxAgeYears ?? '')
      setSelectedSpecialties(savedViewState.selectedSpecialties || [])
      return
    }

    setFiltersInitialized(false)
    setSortBy('htms')
    setSortOrder('desc')
    setSecondarySort(null)
    setFilterOnlyInvalid(false)
    setManageMode(false)
    setHiddenColumnsByPosition({})
    setMinAgeYears('')
    setMaxAgeYears('')
    setSelectedSpecialties([])
  }, [id, savedViewState])

  // Tallenna näkymätila Reduxiin listakohtaisesti
  useEffect(() => {
    if (!id) return

    dispatch(
      setCandidateListViewState({
        id,
        viewState: {
          teamTypeFilter,
          seasonFilter,
          filtersInitialized,
          sortBy,
          sortOrder,
          secondarySort,
          filterOnlyInvalid,
          manageMode,
          hiddenColumnsByPosition,
          minAgeYears,
          maxAgeYears,
          selectedSpecialties,
        },
      })
    )
  }, [
    id,
    teamTypeFilter,
    seasonFilter,
    filtersInitialized,
    sortBy,
    sortOrder,
    secondarySort,
    filterOnlyInvalid,
    manageMode,
    hiddenColumnsByPosition,
    minAgeYears,
    maxAgeYears,
    selectedSpecialties,
    dispatch,
  ])

  const formatCandidateName = candidateItem => {
    const teamPrefix = candidateItem?.TeamType
      ? `${candidateItem.TeamType} `
      : ''

    if (
      candidateItem.TeamType === 'MJ' &&
      candidateItem.Qualifier &&
      candidateItem.Qualifier.trim()
    ) {
      return `${teamPrefix}${
        candidateItem.Name
      } (${candidateItem.Qualifier.trim()})`
    }
    return `${teamPrefix}${candidateItem.Name}`
  }

  const getHiddenColumnsForCandidate = candidateObj => {
    const positionKey = normalizePositionKey(candidateObj?.Position)
    const configured = hiddenColumnsByPosition[positionKey]
    const defaults = DEFAULT_HIDDEN_COLUMNS_BY_POSITION[positionKey] || []
    return new Set(configured || defaults)
  }

  const toggleColumnVisibility = (candidateObj, columnKey) => {
    const positionKey = normalizePositionKey(candidateObj?.Position)
    const defaults = DEFAULT_HIDDEN_COLUMNS_BY_POSITION[positionKey] || []

    setHiddenColumnsByPosition(prev => {
      const current = new Set(prev[positionKey] || defaults)

      if (current.has(columnKey)) {
        current.delete(columnKey)
      } else {
        current.add(columnKey)
      }

      return {
        ...prev,
        [positionKey]: [...current],
      }
    })
  }

  const SpecialtyCell = ({ specialty }) => {
    const label = specArray[specialty] || 'No specialty'

    return (
      <img
        src={getSpecialtyIconPath(specialty)}
        alt={label}
        title={label}
        style={{
          width: '16px',
          height: '16px',
          objectFit: 'contain',
          display: 'block',
          margin: '0 auto',
        }}
      />
    )
  }

  const toggleSelectedSpecialty = specialty => {
    setSelectedSpecialties(prev =>
      prev.includes(specialty)
        ? prev.filter(value => value !== specialty)
        : [...prev, specialty]
    )
  }

  useEffect(() => {
    const fetchAllCandidates = async () => {
      try {
        setIsLoadingHeader(true)
        const loggedUser = window.localStorage.getItem('loggedUser')
        const token = loggedUser ? JSON.parse(loggedUser) : null

        if (!token) {
          console.error('No token available')
          setIsLoadingHeader(false)
          return
        }

        const result = await dispatch(initializeCandidates(token))
        setAllCandidates(result)
      } catch (err) {
        console.error('Error fetching all candidates:', err)
      } finally {
        setIsLoadingHeader(false)
      }
    }
    // Jos on jo ladattu, käytä cachea
    if (candidatesLoaded) {
      setAllCandidates(cachedCandidates)
      setIsLoadingHeader(false)
    } else {
      fetchAllCandidates()
    }
  }, [candidatesLoaded, cachedCandidates, dispatch])

  // Hae niputukset
  useEffect(() => {
    const fetchBundles = async () => {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const token = loggedUser ? JSON.parse(loggedUser) : null
      if (token) {
        await dispatch(initializeBundles(token))
      }
    }
    fetchBundles()
  }, [dispatch])

  // Alusta filtterit nykyisen URL-listan mukaan, jotta sivu ei vaihda väärään listaan
  useEffect(() => {
    if (filtersInitialized) return
    if (!id || allCandidates.length === 0) return

    const currentCandidate = allCandidates.find(c => (c.id || c._id) === id)
    if (currentCandidate) {
      const targetType = currentCandidate.TeamType || 'U21'
      setTeamTypeFilter(targetType)
      setSeasonFilter(
        targetType === 'U21' && currentCandidate.Season
          ? currentCandidate.Season
          : null
      )
      setFiltersInitialized(true)
      return
    }

    // Jos id:tä ei löydy, käytä tallennettua tyyppiä
    setTeamTypeFilter(getInitialTeamTypeFilter())
    setFiltersInitialized(true)
  }, [filtersInitialized, id, allCandidates])

  // Säilytä MJ/U21 valinta näkymien välillä
  useEffect(() => {
    try {
      window.localStorage.setItem(TEAM_TYPE_FILTER_STORAGE_KEY, teamTypeFilter)
    } catch {
      // ignore localStorage errors
    }
  }, [teamTypeFilter])

  // Hae ehdokaslista (cachesta tai backendistä)
  useEffect(() => {
    const fetchCandidate = async () => {
      // Tarkista että id on olemassa
      if (!id) {
        setError('Listan ID puuttuu')
        setIsLoadingPlayers(false)
        return
      }

      setIsLoadingPlayers(true)
      try {
        const loggedUser = window.localStorage.getItem('loggedUser')
        const token = loggedUser ? JSON.parse(loggedUser) : null

        if (!token) {
          setError('Ei kirjautumistietoja')
          setIsLoadingPlayers(false)
          return
        }

        // Jos niputettu, lataa kaikki nipun listat
        if (currentBundle) {
          const bundledData = []
          for (const bc of currentBundle.candidates) {
            const bcId = bc.id || bc._id || bc
            const data = await dispatch(fetchCandidateDetail(bcId, token))
            if (data) {
              // Tarkista harjoitussuunnitelmat
              const checks = {}
              const playersNeedingCheck = []

              for (const entry of data.Players || []) {
                const player = entry.player
                if (!player) continue
                if (
                  player.scoutTrainingPlan &&
                  player.scoutTrainingPlan.finalLevels
                ) {
                  if (
                    player.trainingPlanCheck &&
                    player.trainingPlanCheck.checkedAt &&
                    player.trainingPlanCheck.result &&
                    !isOld(player.trainingPlanCheck.checkedAt)
                  ) {
                    checks[player.playerId] = player.trainingPlanCheck.result
                  } else {
                    playersNeedingCheck.push(player)
                  }
                }
              }

              for (const player of playersNeedingCheck) {
                try {
                  const checkResponse = await axios.post(
                    `/api/players/check-training-plan/${player.playerId}`,
                    {},
                    { headers: { 'x-oauth-token': token } }
                  )
                  checks[player.playerId] = checkResponse.data
                } catch (err) {
                  console.error(
                    `Error checking plan for ${player.playerId}:`,
                    err
                  )
                  checks[player.playerId] = { achievable: null }
                }
              }

              bundledData.push({
                candidate: data,
                players: getValidPlayerEntries(data.Players),
                trainingStandards: data.trainingStandards || null,
                trainingChecks: checks,
              })
            }
          }
          setBundledCandidates(bundledData)

          // Aseta ensimmäinen lista päälistaksi
          if (bundledData.length > 0) {
            setCandidate(bundledData[0].candidate)
            setPlayers(bundledData[0].players)
            setTrainingStandards(bundledData[0].trainingStandards)
            setTrainingChecks(bundledData[0].trainingChecks)
          }
        } else {
          // Ei niputettu, normaali lataus
          setBundledCandidates([])

          // Hae Reduxista tai backendistä
          const data = await dispatch(fetchCandidateDetail(id, token))
          if (data) {
            setCandidate(data)
            setPlayers(getValidPlayerEntries(data.Players))
            setTrainingStandards(data.trainingStandards || null)
            //console.log(data)

            // Tarkista harjoitussuunnitelmat pelaajille
            const checks = {}
            const playersNeedingCheck = []

            for (const entry of data.Players || []) {
              const player = entry.player
              if (!player) continue
              if (
                player.scoutTrainingPlan &&
                player.scoutTrainingPlan.finalLevels
              ) {
                if (
                  player.trainingPlanCheck &&
                  player.trainingPlanCheck.checkedAt &&
                  player.trainingPlanCheck.result &&
                  !isOld(player.trainingPlanCheck.checkedAt)
                ) {
                  checks[player.playerId] = player.trainingPlanCheck.result
                } else {
                  playersNeedingCheck.push(player)
                }
              }
            }

            for (const player of playersNeedingCheck) {
              try {
                const checkResponse = await axios.post(
                  `/api/players/check-training-plan/${player.playerId}`,
                  {},
                  { headers: { 'x-oauth-token': token } }
                )
                checks[player.playerId] = checkResponse.data
              } catch (err) {
                console.error(
                  `Error checking plan for ${player.playerId}:`,
                  err
                )
                checks[player.playerId] = { achievable: null }
              }
            }
            setTrainingChecks(checks)
          } else {
            setError('Listan lataaminen epäonnistui.')
          }
        }
      } catch (err) {
        console.error('Error fetching candidate:', err)
        if (err?.response?.status === 403) {
          setError('Ei oikeuksia listalle.')
        } else {
          setError('Listan lataaminen epäonnistui.')
        }
      } finally {
        setIsLoadingPlayers(false)
      }
    }

    if (id) {
      fetchCandidate()
      loginUser(dispatch)
    }
  }, [id, dispatch, currentBundle])

  // Navigoi automaattisesti ensimmäiseen sopivaan listaan kun filter muuttuu
  useEffect(() => {
    if (!filtersInitialized) return
    if (allCandidates.length === 0) return

    // Suodata listat nykyisten filterien mukaan
    const filtered = allCandidates.filter(c => {
      if (c.TeamType !== teamTypeFilter) return false
      if (teamTypeFilter === 'U21' && seasonFilter && c.Season !== seasonFilter)
        return false
      return true
    })

    // Jos nykyinen lista ei ole suodatetuissa, navigoi ensimmäiseen
    const currentInFiltered = filtered.some(c => (c.id || c._id) === id)

    if (!currentInFiltered && filtered.length > 0) {
      const firstId = filtered[0].id || filtered[0]._id
      navigate(`/candidates/${firstId}`)
    }
  }, [
    filtersInitialized,
    teamTypeFilter,
    seasonFilter,
    allCandidates,
    id,
    navigate,
  ])

  // Vaihda ehdokaslistaa
  const handleCandidateChange = e => {
    const newId = e.target.value
    if (newId) {
      navigate(`/candidates/${newId}`)
    }
  }

  // Tarkista pelaajan harjoitusasetusten validiteetti
  const checkPlayerValidity = entry => {
    const player = entry.player
    if (!player) return false
    const standards = trainingStandards || {
      coach: { target: 5, tolerance: 0 },
      assistants: { target: 10, tolerance: 0 },
      intensity: { target: 100, tolerance: 0 },
      stamina: { target: 10, tolerance: 0 },
      minutes: { target: 90, tolerance: 0 },
      efficiency: { target: 1, tolerance: 0 },
      medic: { target: 5, tolerance: 0 },
      formCoach: { target: 0, tolerance: 0 },
    }

    const data = player.trainingData || {}

    const checkOne = (value, standardKey) => {
      const standard = standards[standardKey]
      if (!standard || value === undefined || value === null) return true
      return Math.abs(value - standard.target) <= standard.tolerance
    }

    return (
      checkOne(data.coach, 'coach') &&
      checkOne(data.assistants, 'assistants') &&
      checkOne(data.intensitety, 'intensity') &&
      checkOne(data.stamina, 'stamina') &&
      checkOne(data.minutes, 'minutes') &&
      checkOne(data.effency, 'efficiency') &&
      checkOne(data.medic, 'medic') &&
      checkOne(data.formCoach, 'formCoach')
    )
  }

  // Laske pelaajan ikä
  const calculateAge = entry => {
    const player = entry.player
    if (!player) return { years: 0, days: 0 }
    return calculateActualAge(player)
  }

  // Sorttaa pelaajat
  const sortPlayers = (playerList, candidateObj = candidate) => {
    const compareEntries = (a, b, field, order) => {
      let compareValue = 0

      const getSkill = (entry, skillName) => {
        return entry.player.playerSkills && entry.player.playerSkills[skillName]
          ? entry.player.playerSkills[skillName]
          : 0
      }

      const getTrainingData = (entry, fieldName) => {
        return entry.player.trainingData && entry.player.trainingData[fieldName]
          ? entry.player.trainingData[fieldName]
          : 0
      }

      const getOwnerLoginName = entry => {
        return (
          entry.player?.ownerInfo?.loginName ||
          entry.player?.owningTeam?.loginName ||
          entry.player?.ownerInfo?.userId ||
          entry.player?.owningTeam?.ownerId ||
          ''
        )
      }

      const getCurrentHTMSValue = entry => {
        const htmsValues = entry.player.HTMS
        if (Array.isArray(htmsValues) && Number.isFinite(htmsValues[0])) {
          return htmsValues[0]
        }
        return 0
      }

      const getPotentialHTMSValue = entry => {
        const htmsValues = entry.player.HTMS
        if (Array.isArray(htmsValues) && Number.isFinite(htmsValues[1])) {
          return htmsValues[1]
        }
        return 0
      }

      const getPositionHTMSValue = entry =>
        calculatePositionHtms(
          entry.player,
          getCandidatePosition(candidateObj),
          getPositionHtmsSkills(candidateObj)
        ) || 0

      switch (field) {
        case 'name':
          compareValue =
            `${a.player.firstName} ${a.player.lastName}`.localeCompare(
              `${b.player.firstName} ${b.player.lastName}`
            )
          break
        case 'age': {
          const ageA = calculateActualAge(a.player)
          const ageB = calculateActualAge(b.player)
          compareValue =
            ageA.years * 112 + ageA.days - (ageB.years * 112 + ageB.days)
          break
        }
        case 'keeperSkill':
          compareValue = getSkill(a, 'keeperSkill') - getSkill(b, 'keeperSkill')
          break
        case 'defenderSkill':
          compareValue =
            getSkill(a, 'defenderSkill') - getSkill(b, 'defenderSkill')
          break
        case 'playmakerSkill':
          compareValue =
            getSkill(a, 'playmakerSkill') - getSkill(b, 'playmakerSkill')
          break
        case 'wingerSkill':
          compareValue = getSkill(a, 'wingerSkill') - getSkill(b, 'wingerSkill')
          break
        case 'passingSkill':
          compareValue =
            getSkill(a, 'passingSkill') - getSkill(b, 'passingSkill')
          break
        case 'scorerSkill':
          compareValue = getSkill(a, 'scorerSkill') - getSkill(b, 'scorerSkill')
          break
        case 'setPiecesSkill':
          compareValue =
            getSkill(a, 'setPiecesSkill') - getSkill(b, 'setPiecesSkill')
          break
        case 'htms':
          compareValue = getCurrentHTMSValue(a) - getCurrentHTMSValue(b)
          break
        case 'positionHtms':
          compareValue = getPositionHTMSValue(a) - getPositionHTMSValue(b)
          break
        case 'potHtms':
          compareValue = getPotentialHTMSValue(a) - getPotentialHTMSValue(b)
          break
        case 'experience':
          compareValue =
            (Number(a.player?.experience) || 0) -
            (Number(b.player?.experience) || 0)
          break
        case 'leadership':
          compareValue =
            (Number(a.player?.leadership) || 0) -
            (Number(b.player?.leadership) || 0)
          break
        case 'tsi':
          compareValue =
            (Number(a.player?.tsi) || 0) - (Number(b.player?.tsi) || 0)
          break
        case 'specialty':
          compareValue =
            (Number(a.player?.specialty) || 0) -
            (Number(b.player?.specialty) || 0)
          break
        case 'playerForm':
          compareValue =
            (Number(a.player?.playerForm) || 0) -
            (Number(b.player?.playerForm) || 0)
          break
        case 'staminaSkill':
          compareValue =
            (Number(a.player?.playerSkills?.staminaSkill) || 0) -
            (Number(b.player?.playerSkills?.staminaSkill) || 0)
          break
        case 'coach':
          compareValue =
            getTrainingData(a, 'coach') - getTrainingData(b, 'coach')
          break
        case 'assistants':
          compareValue =
            getTrainingData(a, 'assistants') - getTrainingData(b, 'assistants')
          break
        case 'intensity':
          compareValue =
            getTrainingData(a, 'intensitety') -
            getTrainingData(b, 'intensitety')
          break
        case 'stamina':
          compareValue =
            getTrainingData(a, 'stamina') - getTrainingData(b, 'stamina')
          break
        case 'medic':
          compareValue =
            getTrainingData(a, 'medic') - getTrainingData(b, 'medic')
          break
        case 'formCoach':
          compareValue =
            getTrainingData(a, 'formCoach') - getTrainingData(b, 'formCoach')
          break
        case 'status':
          compareValue =
            (checkPlayerValidity(a) ? 1 : 0) - (checkPlayerValidity(b) ? 1 : 0)
          break
        case 'owner':
          compareValue = getOwnerLoginName(a).localeCompare(
            getOwnerLoginName(b),
            'fi',
            { sensitivity: 'base' }
          )
          break
        default:
          break
      }

      return order === 'asc' ? compareValue : -compareValue
    }

    return [...playerList].sort((a, b) => {
      const primaryCompare = compareEntries(a, b, sortBy, sortOrder)
      if (primaryCompare !== 0) {
        return primaryCompare
      }

      if (secondarySort?.by) {
        return compareEntries(a, b, secondarySort.by, secondarySort.order)
      }

      return 0
    })
  }

  // Filtteröi pelaajat
  const filterPlayers = playerList => {
    let filtered = getValidPlayerEntries(playerList)

    const minAge = minAgeYears === '' ? null : Number(minAgeYears)
    const maxAge = maxAgeYears === '' ? null : Number(maxAgeYears)

    if (Number.isFinite(minAge)) {
      filtered = filtered.filter(entry => calculateAge(entry).years >= minAge)
    }

    if (Number.isFinite(maxAge)) {
      filtered = filtered.filter(entry => calculateAge(entry).years <= maxAge)
    }

    if (selectedSpecialties.length > 0) {
      filtered = filtered.filter(entry => {
        const specialty = Number(entry?.player?.specialty)
        return (
          Number.isFinite(specialty) && selectedSpecialties.includes(specialty)
        )
      })
    }

    // Piilota piilotetut pelaajat jos ei hallinnoi-tilassa
    if (!manageMode) {
      filtered = filtered.filter(entry => !entry.hidden)
    }

    // Näytä vain virheelliset jos filtteri päällä
    if (filterOnlyInvalid) {
      filtered = filtered.filter(entry => !checkPlayerValidity(entry))
    }

    return filtered
  }

  // Käsittele sorttauksen muutos
  const handleSortChange = criteria => {
    if (sortBy === criteria) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSecondarySort({ by: sortBy, order: sortOrder })
      setSortBy(criteria)
      setSortOrder('desc')
    }
  }

  // Lisää pelaajia ID:n perusteella
  const handleAddPlayers = async playerIds => {
    const loggedUser = window.localStorage.getItem('loggedUser')
    const token = loggedUser ? JSON.parse(loggedUser) : null

    const errors = []
    const added = []

    for (const playerId of playerIds) {
      try {
        const response = await axios.put(
          `/api/candidates/${id}`,
          { playerId },
          {
            headers: {
              'x-oauth-token': token,
            },
          }
        )
        if (response.data && response.data.Players) {
          added.push(playerId)
        }
      } catch (err) {
        errors.push(playerId)
        console.error(`Error adding player ${playerId}:`, err)
      }
    }

    if (added.length > 0) {
      const refreshResponse = await axios.get(`/api/candidates/${id}`, {
        headers: {
          'x-oauth-token': token,
        },
      })
      setPlayers(getValidPlayerEntries(refreshResponse.data.Players))
      // Päivitä Redux-cache
      dispatch(setCandidateDetail({ id, data: refreshResponse.data }))
      dispatch(
        updateCandidatePlayerCount({
          id,
          count: (refreshResponse.data.Players || []).length,
        })
      )
    }

    if (errors.length > 0) {
      throw new Error(`Seuraavat ID:t epäonnistuivat: ${errors.join(', ')}`)
    }
  }

  const refreshCandidateAfterCsvSync = async targetId => {
    const loggedUser = window.localStorage.getItem('loggedUser')
    const token = loggedUser ? JSON.parse(loggedUser) : null
    if (!token || !targetId) return

    const refreshResponse = await axios.get(`/api/candidates/${targetId}`, {
      headers: { 'x-oauth-token': token },
    })

    dispatch(setCandidateDetail({ id: targetId, data: refreshResponse.data }))
    dispatch(
      updateCandidatePlayerCount({
        id: targetId,
        count: (refreshResponse.data.Players || []).length,
      })
    )

    if (isBundled) {
      setBundledCandidates(prev =>
        prev.map(bc => {
          const bcId = bc.candidate.id || bc.candidate._id
          if (bcId === targetId) {
            return {
              ...bc,
              candidate: refreshResponse.data,
              players: getValidPlayerEntries(refreshResponse.data.Players),
            }
          }
          return bc
        })
      )
    } else {
      setCandidate(refreshResponse.data)
      setPlayers(getValidPlayerEntries(refreshResponse.data.Players))
    }
  }

  const handleCsvSynced = async () => {
    const targetId = activeBundleListId || id
    await refreshCandidateAfterCsvSync(targetId)
  }

  const isFavoriteCandidateList = candidateId =>
    favoriteCandidateIds.has(String(candidateId))

  const toggleFavoriteCandidateList = async candidateId => {
    if (!candidateId) return

    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const oauthToken = loggedUser ? JSON.parse(loggedUser) : null
      const isFavorite = isFavoriteCandidateList(candidateId)
      const response = isFavorite
        ? await axios.delete(`/users/favorite-candidate/${candidateId}`, {
            headers: { 'x-oauth-token': oauthToken },
          })
        : await axios.put(
            `/users/favorite-candidate/${candidateId}`,
            {},
            { headers: { 'x-oauth-token': oauthToken } }
          )

      dispatch(
        changeUser({
          ...user,
          favoriteCandidateLists: response.data.favoriteCandidateLists,
        })
      )
    } catch (err) {
      console.error('Error updating candidate list favorite:', err)
      setError('Suosikin p\u00e4ivitys ep\u00e4onnistui')
    }
  }

  const handleCopyList = async copyOptions => {
    const sourceBundleEntry =
      isBundled && copySourceListId
        ? bundledCandidates.find(
            bc => (bc.candidate.id || bc.candidate._id) === copySourceListId
          )
        : null

    const sourceCandidateObj = sourceBundleEntry
      ? sourceBundleEntry.candidate
      : candidate
    const sourceEntries = sourceBundleEntry
      ? sourceBundleEntry.players
      : players

    if (!sourceCandidateObj) {
      throw new Error('Lähdelistaa ei löytynyt')
    }

    const excluded = new Set((copyOptions.excludedIds || []).map(String))
    const copiedPlayerIds = [
      ...new Set(
        (sourceEntries || [])
          .filter(
            entry =>
              !excluded.has(String(entry._id || entry.player?.playerId || ''))
          )
          .map(entry => Number(entry.player?.playerId))
          .filter(Number.isFinite)
      ),
    ]

    const loggedUser = window.localStorage.getItem('loggedUser')
    const token = loggedUser ? JSON.parse(loggedUser) : null

    if (!token) {
      throw new Error('Ei kirjautumistietoja')
    }

    const createdResponse = await axios.post(
      '/api/candidates',
      {
        Name: copyOptions.name,
        TeamType: copyOptions.teamType,
        Season: copyOptions.teamType === 'U21' ? copyOptions.season : null,
        Competition:
          copyOptions.teamType === 'U21' ? copyOptions.competition : null,
        Position: sourceCandidateObj.Position || copyOptions.name,
        positionHtmsSkills: sourceCandidateObj.positionHtmsSkills,
        Qualifier:
          copyOptions.teamType === 'MJ' && copyOptions.qualifier
            ? copyOptions.qualifier
            : undefined,
      },
      {
        headers: {
          'x-oauth-token': token,
        },
      }
    )

    const createdCandidate = createdResponse.data
    const newCandidateId = createdCandidate.id || createdCandidate._id

    if (!newCandidateId) {
      throw new Error('Uuden listan ID puuttuu')
    }

    if (copiedPlayerIds.length > 0) {
      await axios.post(
        `/api/candidates/${newCandidateId}/add-players`,
        { playerIds: copiedPlayerIds },
        { headers: { 'x-oauth-token': token } }
      )
    }

    const refreshResponse = await axios.get(
      `/api/candidates/${newCandidateId}`,
      {
        headers: { 'x-oauth-token': token },
      }
    )

    const refreshedCandidate = refreshResponse.data
    const playersCount = (refreshedCandidate.Players || []).length

    dispatch(
      addCandidate({
        ...createdCandidate,
        playersCount,
      })
    )
    dispatch(
      setCandidateDetail({
        id: String(newCandidateId),
        data: refreshedCandidate,
      })
    )

    setAllCandidates(prev => {
      const normalizedId = String(newCandidateId)
      const alreadyExists = prev.some(
        item => String(item.id || item._id) === normalizedId
      )

      if (alreadyExists) {
        return prev.map(item =>
          String(item.id || item._id) === normalizedId
            ? { ...item, ...createdCandidate, playersCount }
            : item
        )
      }

      return [...prev, { ...createdCandidate, playersCount }]
    })

    setTeamTypeFilter(copyOptions.teamType)
    if (copyOptions.teamType === 'U21') {
      setSeasonFilter(copyOptions.season || null)
    } else {
      setSeasonFilter(null)
    }

    setShowCopyModal(false)
    setCopySourceListId(null)
    navigate(`/candidates/${newCandidateId}`)
  }

  const handleRemovePlayersByPaint = async ({ selectedIds }) => {
    const targetListId = removeSourceListId || id
    const ids = (selectedIds || []).map(String).filter(Boolean)

    if (!targetListId) {
      throw new Error('Listan ID puuttuu')
    }

    if (ids.length === 0) {
      throw new Error('Valitse vähintään yksi pelaaja poistettavaksi')
    }

    const loggedUser = window.localStorage.getItem('loggedUser')
    const token = loggedUser ? JSON.parse(loggedUser) : null

    if (!token) {
      throw new Error('Ei kirjautumistietoja')
    }

    const failed = []

    for (const entryId of ids) {
      try {
        await axios.put(
          `/api/candidates/remove/${targetListId}`,
          { playerId: entryId },
          { headers: { 'x-oauth-token': token } }
        )
      } catch (err) {
        failed.push(entryId)
      }
    }

    await refreshCandidateAfterCsvSync(targetListId)

    setShowRemovePaintModal(false)
    setRemoveSourceListId(null)

    if (failed.length > 0) {
      throw new Error(
        `Kaikkia pelaajia ei saatu poistettua (${failed.length} kpl).`
      )
    }
  }

  const applyTypeSeasonUpdate = updatedCandidate => {
    if (!updatedCandidate) return

    const targetId = String(updatedCandidate.id || updatedCandidate._id || id)

    dispatch(setCandidateDetail({ id: targetId, data: updatedCandidate }))
    dispatch(
      updateCandidateSummary({
        id: targetId,
        patch: {
          Name: updatedCandidate.Name,
          TeamType: updatedCandidate.TeamType,
          Season: updatedCandidate.Season,
          Competition: updatedCandidate.Competition,
          Qualifier: updatedCandidate.Qualifier,
        },
      })
    )

    setAllCandidates(prev =>
      prev.map(c =>
        String(c.id || c._id) === targetId
          ? {
              ...c,
              Name: updatedCandidate.Name,
              TeamType: updatedCandidate.TeamType,
              Season: updatedCandidate.Season,
              Competition: updatedCandidate.Competition,
              Qualifier: updatedCandidate.Qualifier,
            }
          : c
      )
    )

    if (activeBundleListId) {
      setBundledCandidates(prev =>
        prev.map(bc => {
          const bcId = String(bc.candidate.id || bc.candidate._id)
          if (bcId === targetId) {
            return {
              ...bc,
              candidate: updatedCandidate,
              players: updatedCandidate.Players || bc.players,
            }
          }
          return bc
        })
      )
      return
    }

    setCandidate(updatedCandidate)
    setPlayers(getValidPlayerEntries(updatedCandidate.Players))
  }

  // Tallenna asetukset (harjoitusvaatimukset ja nimi)
  const handleSaveStandards = async (standards, newName) => {
    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const token = loggedUser ? JSON.parse(loggedUser) : null

      // Tallenna harjoitusvaatimukset
      await axios.put(
        `/api/candidates/${id}/standards`,
        { trainingStandards: standards },
        {
          headers: {
            'x-oauth-token': token,
          },
        }
      )
      setTrainingStandards(standards)
      // Päivitä Redux-cache
      if (candidate) {
        dispatch(
          setCandidateDetail({
            id,
            data: { ...candidate, trainingStandards: standards },
          })
        )
      }

      // Tallenna nimi jos muuttunut
      if (newName && newName !== candidate.Name) {
        const nameResponse = await axios.put(
          `/api/candidates/${id}/name`,
          { name: newName },
          {
            headers: {
              'x-oauth-token': token,
            },
          }
        )
        setCandidate(nameResponse.data)
        // Päivitä Redux-cache
        dispatch(setCandidateDetail({ id, data: nameResponse.data }))
        // Päivitä myös allCandidates-lista (local + Redux)
        setAllCandidates(prev =>
          prev.map(c =>
            (c.id || c._id) === id
              ? {
                  ...c,
                  Name: nameResponse.data?.Name || newName,
                  TeamType: nameResponse.data?.TeamType ?? c.TeamType,
                  Season: nameResponse.data?.Season ?? c.Season,
                  Competition: nameResponse.data?.Competition ?? c.Competition,
                  Qualifier: nameResponse.data?.Qualifier ?? c.Qualifier,
                }
              : c
          )
        )
        dispatch(
          updateCandidateSummary({
            id,
            patch: {
              Name: nameResponse.data?.Name || newName,
              TeamType: nameResponse.data?.TeamType,
              Season: nameResponse.data?.Season,
              Competition: nameResponse.data?.Competition,
              Qualifier: nameResponse.data?.Qualifier,
            },
          })
        )
      }
    } catch (err) {
      console.error('Error saving settings:', err)
      throw new Error('Asetusten tallentaminen epäonnistui')
    }
  }

  const handlePositionHtmsSkillsChange = async skill => {
    if (!candidate) return

    const currentSkills = getPositionHtmsSkills(candidate)
    const positionHtmsSkills = currentSkills.includes(skill)
      ? currentSkills.filter(currentSkill => currentSkill !== skill)
      : [...currentSkills, skill]

    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const token = loggedUser ? JSON.parse(loggedUser) : null
      await axios.put(
        `/api/candidates/${id}/position-htms-skills`,
        { positionHtmsSkills },
        { headers: { 'x-oauth-token': token } }
      )
      const updatedCandidate = { ...candidate, positionHtmsSkills }
      setCandidate(updatedCandidate)
      dispatch(setCandidateDetail({ id, data: updatedCandidate }))
    } catch (err) {
      console.error('Error saving HTMS position skills:', err)
    }
  }

  // Lisää pelaajia niputetun listan tiettyyn listaan
  const handleAddPlayersToBundle = async playerIds => {
    const targetId = activeBundleListId
    if (!targetId) return

    const loggedUser = window.localStorage.getItem('loggedUser')
    const token = loggedUser ? JSON.parse(loggedUser) : null

    const errors = []
    const added = []

    for (const playerId of playerIds) {
      try {
        const response = await axios.put(
          `/api/candidates/${targetId}`,
          { playerId },
          { headers: { 'x-oauth-token': token } }
        )
        if (response.data && response.data.Players) {
          added.push(playerId)
        }
      } catch (err) {
        errors.push(playerId)
      }
    }

    if (added.length > 0) {
      const refreshResponse = await axios.get(`/api/candidates/${targetId}`, {
        headers: { 'x-oauth-token': token },
      })
      // Päivitä bundledCandidates
      setBundledCandidates(prev =>
        prev.map(bc => {
          const bcId = bc.candidate.id || bc.candidate._id
          if (bcId === targetId) {
            return {
              ...bc,
              candidate: refreshResponse.data,
              players: getValidPlayerEntries(refreshResponse.data.Players),
            }
          }
          return bc
        })
      )
      dispatch(setCandidateDetail({ id: targetId, data: refreshResponse.data }))
      dispatch(
        updateCandidatePlayerCount({
          id: targetId,
          count: (refreshResponse.data.Players || []).length,
        })
      )
    }

    if (errors.length > 0) {
      throw new Error(`Seuraavat ID:t epäonnistuivat: ${errors.join(', ')}`)
    }
  }

  // Tallenna asetukset niputetun listan tietylle listalle
  const handleSaveStandardsForBundle = async (targetId, standards, newName) => {
    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const token = loggedUser ? JSON.parse(loggedUser) : null

      await axios.put(
        `/api/candidates/${targetId}/standards`,
        { trainingStandards: standards },
        { headers: { 'x-oauth-token': token } }
      )

      let updatedCandidate = null
      if (newName) {
        const bc = bundledCandidates.find(
          bc => (bc.candidate.id || bc.candidate._id) === targetId
        )
        if (bc && newName !== bc.candidate.Name) {
          const nameResponse = await axios.put(
            `/api/candidates/${targetId}/name`,
            { name: newName },
            { headers: { 'x-oauth-token': token } }
          )
          updatedCandidate = nameResponse.data
        }
      }

      setBundledCandidates(prev =>
        prev.map(bc => {
          const bcId = bc.candidate.id || bc.candidate._id
          if (bcId === targetId) {
            return {
              ...bc,
              trainingStandards: standards,
              candidate: updatedCandidate || {
                ...bc.candidate,
                trainingStandards: standards,
              },
            }
          }
          return bc
        })
      )

      if (updatedCandidate) {
        dispatch(
          updateCandidateSummary({
            id: targetId,
            patch: {
              Name: updatedCandidate.Name,
              TeamType: updatedCandidate.TeamType,
              Season: updatedCandidate.Season,
              Competition: updatedCandidate.Competition,
              Qualifier: updatedCandidate.Qualifier,
            },
          })
        )
      }
    } catch (err) {
      console.error('Error saving settings:', err)
      throw new Error('Asetusten tallentaminen epäonnistui')
    }
  }

  // Valmistele näytettävät pelaajat
  const displayPlayers = sortPlayers(filterPlayers(players))

  // Laske statistiikat
  const validCount = players.filter(p => checkPlayerValidity(p)).length
  const invalidCount = players.length - validCount

  // Sarakkeen otsikko + sorttausnuoli
  const SortableHeader = ({ field, label, align = 'center', tooltip }) => (
    <th
      onClick={() => handleSortChange(field)}
      title={tooltip}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap', textAlign: align }}
    >
      {label} {sortBy === field && (sortOrder === 'asc' ? '▲' : '▼')}
    </th>
  )

  /**
   * Tarkistaa täsmääkö nykyinen harjoitusmuoto scoutTrainingPlanin taitoihin
   * ja ovatko trainhistoryn merkinnät suunnitelman jälkeen oikeita.
   */
  const getTrainingModeCheck = player => {
    const plan = player.scoutTrainingPlan
    const data = player.trainingData || {}
    const currentMode = data.mode ? Number(data.mode) : null

    // Jos ei ole suunnitelmaa, skillWeeksiä, datea tai trainhistorya, ei tarkisteta
    const history = player.trainhistory || []
    if (
      !plan ||
      !plan.skillWeeks ||
      plan.skillWeeks.length === 0 ||
      !plan.date
    ) {
      return { hasPlan: false }
    }
    if (history.length === 0) {
      return { hasPlan: false }
    }

    // Kerää suunnitelman sallitut taidot ja viikkorajat skillWeeksistä
    const allowedSkills = plan.skillWeeks.map(sw => sw.skill)
    const weekLimits = {}
    plan.skillWeeks.forEach(sw => {
      weekLimits[sw.skill] = sw.weeks
    })

    // Tarkista nykyinen harjoitusmuoto
    let currentModeMatch = null
    if (currentMode !== null) {
      const currentSkill = modeToSkillMap[currentMode - 2]
      currentModeMatch = currentSkill
        ? allowedSkills.includes(currentSkill)
        : false
    }

    // Tarkista trainhistory suunnitelman date:n jälkeen
    const planDate = new Date(plan.date)
    const mismatchWeeks = []
    const overflowWeeks = []

    // Laske jokaisen taidon harjoitusviikot suunnitelman jälkeen
    const skillWeekCounts = {}

    history.forEach(entry => {
      if (!entry || !entry.date) return
      const entryDate = new Date(entry.date)
      if (entryDate > planDate) {
        const entryMode = Number(entry.mode)
        const entrySkill = modeToSkillMap[entryMode - 2]
        const weekStart = new Date(entryDate.getFullYear(), 0, 1)
        const weekNum = Math.ceil(
          ((entryDate - weekStart) / 86400000 + weekStart.getDay() + 1) / 7
        )
        const modeName = taidotLyh[entryMode - 2] || `mode ${entryMode}`

        // Tarkista onko harjoitusmuoto sallittu suunnitelmassa
        if (!entrySkill || !allowedSkills.includes(entrySkill)) {
          mismatchWeeks.push({
            week: weekNum,
            mode: modeName,
            date: entryDate,
            reason: 'wrong_skill',
          })
        } else {
          // Laske viikkomäärä tälle taidolle
          skillWeekCounts[entrySkill] = (skillWeekCounts[entrySkill] || 0) + 1
          const limit = weekLimits[entrySkill]
          if (skillWeekCounts[entrySkill] > limit) {
            overflowWeeks.push({
              week: weekNum,
              mode: modeName,
              date: entryDate,
              reason: 'overflow',
              skill: entrySkill,
              count: skillWeekCounts[entrySkill],
              limit: limit,
            })
          }
        }
      }
    })

    const allProblems = [...mismatchWeeks, ...overflowWeeks]

    return {
      hasPlan: true,
      currentModeMatch,
      mismatchWeeks: allProblems,
      allMatch: allProblems.length === 0,
    }
  }

  // Renderöi yksittäinen pelaajarivi (käytetään sekä normaalissa että niputetussa näkymässä)
  const renderPlayerRow = (
    entry,
    rowStandards,
    rowChecks,
    listId,
    candidateObj,
    hiddenColumns,
    showTsiDirection
  ) => {
    const player = entry.player
    const { years: age, days } = calculateAge(entry)
    const data = player.trainingData || {}
    const skills = player.playerSkills || {}
    const skillColor = player.ownerMissingInDb ? '#f39c12' : undefined
    const monthOld = isOverMonthOld(player.dateUpdated)
    const playerOld = isOld(player.dateUpdated)
    const tournamentInfo = getTournamentFromCandidate(candidateObj)
    const u21Elig = tournamentInfo
      ? getU21TournamentEligibility(
          age,
          days,
          tournamentInfo.type,
          tournamentInfo.tournament
        )
      : null

    const RowStatusIcon = ({ value, standardKey }) => {
      const defaultStandards = {
        coach: { target: 5, tolerance: 0 },
        assistants: { target: 10, tolerance: 0 },
        intensity: { target: 100, tolerance: 0 },
        stamina: { target: 10, tolerance: 0 },
        minutes: { target: 90, tolerance: 0 },
        efficiency: { target: 1, tolerance: 0 },
        medic: { target: 5, tolerance: 0 },
        formCoach: { target: 0, tolerance: 0 },
      }
      const standards = { ...defaultStandards, ...(rowStandards || {}) }
      const standard = standards[standardKey]
      if (value === undefined || value === null) {
        return <span style={{ color: '#888' }}>-</span>
      }
      const isOkVal =
        standard && Math.abs(value - standard.target) <= standard.tolerance
      return (
        <span style={{ color: isOkVal ? '#2ecc71' : '#e74c3c' }}>{value}</span>
      )
    }

    const RowTrainingPlanStatus = () => {
      const check = rowChecks[player.playerId]
      if (!check) return <span style={{ color: '#888' }}>-</span>
      if (check.achievable === null)
        return <span style={{ color: '#888' }}>-</span>
      const isOkVal = check.achievable || check.weeksDifference >= -1
      return (
        <span
          style={{ color: isOkVal ? '#2ecc71' : '#e74c3c' }}
          title={check.message}
        >
          {isOkVal ? '✓' : '✗'}
        </span>
      )
    }

    const displayHTMS =
      Array.isArray(player.HTMS) && Number.isFinite(player.HTMS[0])
        ? player.HTMS[0]
        : null

    const displayPotentialHTMS =
      Array.isArray(player.HTMS) && Number.isFinite(player.HTMS[1])
        ? player.HTMS[1]
        : null

    const position = getCandidatePosition(candidateObj)
    const displayPositionHTMS = calculatePositionHtms(
      player,
      position,
      getPositionHtmsSkills(candidateObj)
    )

    const displayExperience =
      Number.isFinite(Number(player.experience)) ||
      Number(player.experience) === 0
        ? Number(player.experience)
        : '-'

    const displayLeadership =
      Number.isFinite(Number(player.leadership)) ||
      Number(player.leadership) === 0
        ? Number(player.leadership)
        : '-'

    const tsiDirection = getEstimatedFormDirection(player)
    const hasFormValue = value =>
      value !== null &&
      value !== undefined &&
      value !== '' &&
      Number.isFinite(Number(value))
    const formHistory = Array.isArray(player.trainhistory)
      ? player.trainhistory.filter(entry => hasFormValue(entry?.form))
      : []
    const latestFormHistory = formHistory[formHistory.length - 1]
    const previousFormHistory =
      Number(latestFormHistory?.form) === Number(player.playerForm)
        ? formHistory[formHistory.length - 2]
        : latestFormHistory
    const formChange =
      hasFormValue(player.playerForm) &&
      hasFormValue(previousFormHistory?.form)
        ? Number(player.playerForm) - Number(previousFormHistory.form)
        : 0
    const formIndicator =
      formChange === 0
        ? ''
        : formChange > 0
          ? '\u2191'.repeat(Math.min(Math.abs(formChange), 2))
          : '\u2193'.repeat(Math.min(Math.abs(formChange), 2))
    const formIndicatorColor =
      formChange > 0 ? '#2ecc71' : formChange < 0 ? '#e74c3c' : undefined

    const injuryBadge = getInjuryBadge(player)

    const showTournamentColumn =
      !!tournamentInfo && !hiddenColumns.has('tournament')

    return (
      <tr
        key={entry._id}
        title={
          playerOld
            ? 'Pelaaja päivitetty viimeksi ' + dateFin(player.dateUpdated)
            : ''
        }
        style={{
          borderLeft: playerOld ? '3px solid #e74c3c' : '3px solid #2ecc71',
        }}
      >
        {!hiddenColumns.has('name') && (
          <td style={{ textAlign: 'left' }}>
            <Link
              to={`/players/${player.playerId}`}
              style={{ color: '#FFFFFF', textDecoration: 'none' }}
            >
              {player.firstName} {player.lastName}
            </Link>
            {injuryBadge && (
              <span
                title={injuryBadge.title}
                style={{
                  marginLeft: '6px',
                  color: injuryBadge.icon === '❌' ? '#e74c3c' : '#f1c40f',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                }}
              >
                {injuryBadge.text}
              </span>
            )}
          </td>
        )}
        {!hiddenColumns.has('playerId') && (
          <td style={{ textAlign: 'left' }}>
            <a
              href={`https://hattrick.org/goto.ashx?path=/Club/Players/Player.aspx?playerId=${player.playerId}`}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#888', fontSize: '0.75rem' }}
            >
              {player.playerId}
            </a>
          </td>
        )}
        {!hiddenColumns.has('age') && (
          <td>
            {age}.{days}
          </td>
        )}
        {showTournamentColumn && u21Elig && (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {u21Elig.tooltip.split('\n').map((line, i) => (
                  <span key={i}>
                    {i > 0 && <br />}
                    {line}
                  </span>
                ))}
              </Tooltip>
            }
          >
            <td
              style={{
                color: u21Elig.color,
                fontSize: '0.8rem',
                cursor: 'default',
              }}
            >
              {u21Elig.text}
            </td>
          </OverlayTrigger>
        )}
        {showTournamentColumn && !u21Elig && <td>-</td>}
        {!hiddenColumns.has('specialty') && (
          <td>
            <SpecialtyCell specialty={player.specialty} />
          </td>
        )}
        {!hiddenColumns.has('playerForm') && (
          <td
            style={{
              color: getFormAndStaminaColor(player.playerForm) || '#FFFFFF',
            }}
          >
            {formatPlayerForm(player.playerForm)}
            {formIndicator && (
              <span style={{ color: formIndicatorColor, marginLeft: '3px' }}>
                {formIndicator}
              </span>
            )}
          </td>
        )}
        {showTsiDirection && (
          <td style={{ color: tsiDirection?.color }}>
            {tsiDirection?.indicator || ''}
          </td>
        )}
        {!hiddenColumns.has('staminaSkill') && (
          <td
            style={{
              color: getFormAndStaminaColor(skills.staminaSkill) || '#FFFFFF',
            }}
          >
            {formatStaminaSkill(skills.staminaSkill)}
          </td>
        )}
        {!hiddenColumns.has('keeperSkill') && (
          <td style={{ color: skillColor }}>
            {skills.keeperSkill ? skills.keeperSkill.toFixed(2) : '-'}
          </td>
        )}
        {!hiddenColumns.has('defenderSkill') && (
          <td style={{ color: skillColor }}>
            {skills.defenderSkill ? skills.defenderSkill.toFixed(2) : '-'}
          </td>
        )}
        {!hiddenColumns.has('playmakerSkill') && (
          <td style={{ color: skillColor }}>
            {skills.playmakerSkill ? skills.playmakerSkill.toFixed(2) : '-'}
          </td>
        )}
        {!hiddenColumns.has('wingerSkill') && (
          <td style={{ color: skillColor }}>
            {skills.wingerSkill ? skills.wingerSkill.toFixed(2) : '-'}
          </td>
        )}
        {!hiddenColumns.has('passingSkill') && (
          <td style={{ color: skillColor }}>
            {skills.passingSkill ? skills.passingSkill.toFixed(2) : '-'}
          </td>
        )}
        {!hiddenColumns.has('scorerSkill') && (
          <td style={{ color: skillColor }}>
            {skills.scorerSkill ? skills.scorerSkill.toFixed(2) : '-'}
          </td>
        )}
        {!hiddenColumns.has('setPiecesSkill') && (
          <td style={{ color: skillColor }}>
            {skills.setPiecesSkill ? skills.setPiecesSkill.toFixed(2) : '-'}
          </td>
        )}
        {!hiddenColumns.has('experience') && <td>{displayExperience}</td>}
        {!hiddenColumns.has('leadership') && <td>{displayLeadership}</td>}
        {!hiddenColumns.has('htms') && (
          <td>{displayHTMS !== null ? displayHTMS : '-'}</td>
        )}
        {!hiddenColumns.has('positionHtms') && (
          <td>{displayPositionHTMS !== null ? displayPositionHTMS : '-'}</td>
        )}
        {!hiddenColumns.has('potHtms') && (
          <td>{displayPotentialHTMS !== null ? displayPotentialHTMS : '-'}</td>
        )}
        {!hiddenColumns.has('coach') && (
          <td>
            {monthOld ? (
              '-'
            ) : (
              <RowStatusIcon value={data.coach} standardKey="coach" />
            )}
          </td>
        )}
        {!hiddenColumns.has('assistants') && (
          <td>
            {monthOld ? (
              '-'
            ) : (
              <RowStatusIcon value={data.assistants} standardKey="assistants" />
            )}
          </td>
        )}
        {!hiddenColumns.has('intensity') && (
          <td>
            {monthOld ? (
              '-'
            ) : (
              <RowStatusIcon value={data.intensitety} standardKey="intensity" />
            )}
          </td>
        )}
        {!hiddenColumns.has('staminaTraining') && (
          <td>
            {monthOld ? (
              '-'
            ) : (
              <RowStatusIcon value={data.stamina} standardKey="stamina" />
            )}
          </td>
        )}
        {!hiddenColumns.has('minutes') && (
          <td>
            {monthOld ? (
              '-'
            ) : (
              <RowStatusIcon value={data.minutes} standardKey="minutes" />
            )}
          </td>
        )}
        {!hiddenColumns.has('efficiency') && (
          <td>
            {monthOld ? (
              '-'
            ) : (
              <RowStatusIcon value={data.effency} standardKey="efficiency" />
            )}
          </td>
        )}
        {!hiddenColumns.has('medic') && (
          <td>
            {monthOld ? (
              '-'
            ) : (
              <RowStatusIcon value={data.medic} standardKey="medic" />
            )}
          </td>
        )}
        {!hiddenColumns.has('formCoach') && (
          <td>
            {monthOld ? (
              '-'
            ) : (
              <RowStatusIcon value={data.formCoach} standardKey="formCoach" />
            )}
          </td>
        )}
        {!hiddenColumns.has('trainingMode') && (
          <td>
            {(() => {
              const modeCheck = getTrainingModeCheck(player)
              const modeLabel = data.mode
                ? taidotLyh[data.mode - 2] || '-'
                : '-'

              if (!modeCheck.hasPlan || !data.mode) {
                return modeLabel
              }

              const modeColor = modeCheck.currentModeMatch
                ? '#2ecc71'
                : '#e74c3c'
              const historyIcon = modeCheck.allMatch ? (
                <span style={{ color: '#2ecc71', marginLeft: '4px' }}>✓</span>
              ) : (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      {modeCheck.mismatchWeeks.map((w, i) => (
                        <div key={i}>
                          vko {w.week}: {w.mode}
                          {w.reason === 'overflow'
                            ? ` (${w.count}/${w.limit} vkoa)`
                            : ' (väärä)'}
                        </div>
                      ))}
                    </Tooltip>
                  }
                >
                  <span
                    style={{
                      color: '#e74c3c',
                      marginLeft: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    ✗
                  </span>
                </OverlayTrigger>
              )

              return (
                <>
                  <span style={{ color: modeColor }}>{modeLabel}</span>
                  {historyIcon}
                </>
              )
            })()}
          </td>
        )}
        {!hiddenColumns.has('trainingPlan') && (
          <td>
            <RowTrainingPlanStatus />
          </td>
        )}
        {!hiddenColumns.has('owner') && (
          <td style={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
            {player.ownerInfo
              ? (() => {
                  const threeWeeksAgo = new Date()
                  threeWeeksAgo.setDate(threeWeeksAgo.getDate() - 21)
                  const lastLogin = player.ownerInfo.lastLoginDate
                    ? new Date(player.ownerInfo.lastLoginDate)
                    : null
                  const isInactive = lastLogin && lastLogin < threeWeeksAgo
                  const ownerUserId =
                    player.ownerInfo.userId ||
                    player.owningTeam?.ownerId ||
                    null
                  const ownerLabel =
                    player.ownerInfo.loginName ||
                    player.owningTeam?.loginName ||
                    (ownerUserId ? `ID ${ownerUserId}` : '')
                  const ownerMissingInDb = !!player.ownerMissingInDb

                  if (!ownerUserId || !ownerLabel) {
                    return '-'
                  }

                  const ownerTitleParts = []
                  if (ownerMissingInDb) {
                    ownerTitleParts.push(
                      'Omistajaa ei löydy Trackerin tietokannasta'
                    )
                  }
                  if (lastLogin) {
                    ownerTitleParts.push(
                      `Viimeksi kirjautunut: ${lastLogin.toLocaleDateString(
                        'fi-FI'
                      )}`
                    )
                  }

                  return (
                    <>
                      <a
                        href={`https://hattrick.org/goto.ashx?path=/Club/Manager/?userId=${ownerUserId}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: ownerMissingInDb
                            ? '#f39c12'
                            : isInactive
                            ? '#e74c3c'
                            : '#FFFFFF',
                          textDecoration: 'none',
                        }}
                        title={ownerTitleParts.join('\n')}
                      >
                        {ownerLabel}
                      </a>{' '}
                      <a
                        href={`https://hattrick.org/goto.ashx?path=/MyHattrick/Inbox/?actionType=newMail&userId=${ownerUserId}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Lähetä viesti"
                      >
                        ✉️
                      </a>
                    </>
                  )
                })()
              : '-'}
          </td>
        )}
        {manageMode && !hiddenColumns.has('manageVisibility') && (
          <td>
            <Button
              variant={entry.hidden ? 'outline-warning' : 'outline-secondary'}
              size="sm"
              onClick={() => handleToggleHiddenForList(entry._id, listId)}
              style={{ padding: '0 5px', fontSize: '0.7rem' }}
              title={entry.hidden ? 'Näytä pelaaja' : 'Piilota pelaaja'}
            >
              {entry.hidden ? '👁' : '🚫'}
            </Button>
          </td>
        )}
        {manageMode && !hiddenColumns.has('manageDelete') && (
          <td>
            {confirmDelete === entry._id ? (
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleRemovePlayerFromList(entry._id, listId)}
                style={{ padding: '0 5px', fontSize: '0.7rem' }}
              >
                Varmista?
              </Button>
            ) : (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setConfirmDelete(entry._id)}
                style={{ padding: '0 5px', fontSize: '0.7rem' }}
              >
                ✗
              </Button>
            )}
          </td>
        )}
      </tr>
    )
  }

  // Toggle hidden pelaaja tietyllä listalla (niputettu tai normaali)
  const handleToggleHiddenForList = async (entryId, listId) => {
    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const token = loggedUser ? JSON.parse(loggedUser) : null
      const targetListId = listId || id

      await axios.put(
        `/api/candidates/${targetListId}/toggle-hidden`,
        { playerId: entryId },
        { headers: { 'x-oauth-token': token } }
      )

      if (isBundled) {
        setBundledCandidates(prev =>
          prev.map(bc => {
            const bcId = bc.candidate.id || bc.candidate._id
            if (bcId === targetListId) {
              return {
                ...bc,
                players: bc.players.map(entry =>
                  entry._id === entryId
                    ? { ...entry, hidden: !entry.hidden }
                    : entry
                ),
              }
            }
            return bc
          })
        )
      } else {
        setPlayers(
          players.map(entry =>
            entry._id === entryId ? { ...entry, hidden: !entry.hidden } : entry
          )
        )
        if (candidate) {
          const updatedPlayers = players.map(entry =>
            entry._id === entryId ? { ...entry, hidden: !entry.hidden } : entry
          )
          dispatch(
            setCandidateDetail({
              id,
              data: { ...candidate, Players: updatedPlayers },
            })
          )
        }
      }
    } catch (err) {
      console.error('Error toggling player visibility:', err)
    }
  }

  // Poista pelaaja tietystä listasta (niputettu tai normaali)
  const handleRemovePlayerFromList = async (entryId, listId) => {
    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const token = loggedUser ? JSON.parse(loggedUser) : null
      const targetListId = listId || id

      await axios.put(
        `/api/candidates/remove/${targetListId}`,
        { playerId: entryId },
        { headers: { 'x-oauth-token': token } }
      )

      if (isBundled) {
        setBundledCandidates(prev =>
          prev.map(bc => {
            const bcId = bc.candidate.id || bc.candidate._id
            if (bcId === targetListId) {
              const updatedPlayers = bc.players.filter(
                entry => entry._id !== entryId
              )
              dispatch(
                setCandidateDetail({
                  id: targetListId,
                  data: { ...bc.candidate, Players: updatedPlayers },
                })
              )
              dispatch(
                updateCandidatePlayerCount({
                  id: targetListId,
                  count: updatedPlayers.length,
                })
              )
              return {
                ...bc,
                players: updatedPlayers,
              }
            }
            return bc
          })
        )
      } else {
        setPlayers(players.filter(entry => entry._id !== entryId))
        if (candidate) {
          const updatedPlayers = players.filter(entry => entry._id !== entryId)
          dispatch(
            setCandidateDetail({
              id,
              data: { ...candidate, Players: updatedPlayers },
            })
          )
          dispatch(
            updateCandidatePlayerCount({ id, count: updatedPlayers.length })
          )
        }
      }
      setConfirmDelete(null)
    } catch (err) {
      console.error('Error removing player:', err)
    }
  }

  if (isLoadingHeader) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" variant="light" />
        <p style={{ color: '#FFFFFF' }}>Ladataan listoja...</p>
      </Container>
    )
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Card style={{ backgroundColor: '#2C2F33', color: '#e74c3c' }}>
          <Card.Body>{error}</Card.Body>
        </Card>
      </Container>
    )
  }

  return (
    <Container fluid className="mt-3 enhanced-candidates-page">
      {/* Header */}
      <Card
        className="mb-3"
        style={{
          backgroundColor: '#23272A',
          color: '#FFFFFF',
          borderColor: '#1E2124',
        }}
      >
        <Card.Body
          className="enhanced-list-header-body"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          <div
            className="enhanced-list-header-left"
            style={{ display: 'flex', alignItems: 'center', gap: '15px' }}
          >
            <BackToListsButton />
            {/* MJ/U21 vaihtokytkin */}
            <div
              style={{
                display: 'flex',
                backgroundColor: '#1E2124',
                borderRadius: '5px',
                overflow: 'hidden',
              }}
            >
              <Button
                variant={teamTypeFilter === 'MJ' ? 'info' : 'outline-secondary'}
                size="sm"
                onClick={() => {
                  setTeamTypeFilter('MJ')
                  setSeasonFilter(null)
                }}
                style={{
                  borderRadius: 0,
                  border: 'none',
                  padding: '5px 15px',
                }}
              >
                MJ
              </Button>
              <Button
                variant={
                  teamTypeFilter === 'U21' ? 'info' : 'outline-secondary'
                }
                size="sm"
                onClick={() => setTeamTypeFilter('U21')}
                style={{
                  borderRadius: 0,
                  border: 'none',
                  padding: '5px 15px',
                }}
              >
                U21
              </Button>
            </div>

            {/* Kausiluettelo (vain U21) */}
            {teamTypeFilter === 'U21' &&
              (() => {
                const seasons = [
                  ...new Set(
                    allCandidates
                      .filter(c => c.TeamType === 'U21' && c.Season)
                      .map(c => c.Season)
                  ),
                ].sort((a, b) => b - a)

                if (seasons.length > 0) {
                  return (
                    <Form.Select
                      value={seasonFilter || ''}
                      onChange={e =>
                        setSeasonFilter(
                          e.target.value ? Number(e.target.value) : null
                        )
                      }
                      style={{
                        backgroundColor: '#1E2124',
                        color: '#FFFFFF',
                        borderColor: '#444',
                        width: 'auto',
                        minWidth: '100px',
                      }}
                      size="sm"
                    >
                      <option value="">Kaikki kaudet</option>
                      {seasons.map(season => (
                        <option key={season} value={season}>
                          Kausi {season}
                        </option>
                      ))}
                    </Form.Select>
                  )
                }
                return null
              })()}

            {/* Ehdokaslistojen valinta */}
            <Form.Select
              value={id}
              onChange={handleCandidateChange}
              style={{
                backgroundColor: '#1E2124',
                color: '#FFFFFF',
                borderColor: '#444',
                width: 'auto',
                minWidth: '200px',
              }}
            >
              {(() => {
                const filtered = allCandidates.filter(c => {
                  if (c.TeamType !== teamTypeFilter) return false
                  if (
                    teamTypeFilter === 'U21' &&
                    seasonFilter &&
                    c.Season !== seasonFilter
                  )
                    return false
                  return true
                })

                // Ryhmittele niputetut ja niputtamattomat
                const bundledIds = new Set()
                const options = []

                for (const bundle of bundles) {
                  const bundleCandidates = bundle.candidates
                    .map(bc => {
                      const bcId = bc.id || bc._id || bc
                      return filtered.find(c => (c.id || c._id) === bcId)
                    })
                    .filter(Boolean)

                  if (bundleCandidates.length > 0) {
                    bundleCandidates.forEach(c => bundledIds.add(c.id || c._id))
                    const names = bundleCandidates.map(c =>
                      formatCandidateName(c)
                    )
                    const displayName =
                      names.length <= 1
                        ? names[0] || ''
                        : names.slice(0, -1).join(', ') +
                          ' ja ' +
                          names[names.length - 1]
                    const firstId =
                      bundleCandidates[0].id || bundleCandidates[0]._id
                    options.push(
                      <option key={`bundle-${bundle.id}`} value={firstId}>
                        📦 {displayName}
                      </option>
                    )
                  }
                }

                filtered.forEach(c => {
                  const cId = c.id || c._id
                  if (!bundledIds.has(cId)) {
                    options.push(
                      <option key={cId} value={cId}>
                        {formatCandidateName(c)}
                      </option>
                    )
                  }
                })

                return options
              })()}
            </Form.Select>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#FFFFFF',
              }}
            >
              <small>Ikä:</small>
              <Form.Control
                size="sm"
                type="number"
                min="0"
                step="1"
                placeholder="ala"
                value={minAgeYears}
                onChange={e => setMinAgeYears(e.target.value)}
                style={{
                  backgroundColor: '#1E2124',
                  color: '#FFFFFF',
                  borderColor: '#444',
                  width: '72px',
                }}
              />
              <small>-</small>
              <Form.Control
                size="sm"
                type="number"
                min="0"
                step="1"
                placeholder="ylä"
                value={maxAgeYears}
                onChange={e => setMaxAgeYears(e.target.value)}
                style={{
                  backgroundColor: '#1E2124',
                  color: '#FFFFFF',
                  borderColor: '#444',
                  width: '72px',
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#FFFFFF',
              }}
            >
              <small>Erik:</small>
              {SPECIALTY_FILTER_OPTIONS.map(specialty => {
                const isSelected = selectedSpecialties.includes(specialty)
                const label = specArray[specialty] || `Erikoisuus ${specialty}`

                return (
                  <button
                    key={specialty}
                    type="button"
                    onClick={() => toggleSelectedSpecialty(specialty)}
                    title={`${label}${isSelected ? ' (valittu)' : ''}`}
                    aria-label={`${label}${
                      isSelected ? ' valittu' : ' ei valittu'
                    }`}
                    style={{
                      border: isSelected
                        ? '1px solid #27ae60'
                        : '1px solid #555',
                      background: isSelected
                        ? 'rgba(46, 204, 113, 0.2)'
                        : 'transparent',
                      borderRadius: '4px',
                      padding: '2px',
                      lineHeight: 0,
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={getSpecialtyIconPath(specialty)}
                      alt={label}
                      style={{
                        width: '16px',
                        height: '16px',
                        objectFit: 'contain',
                        filter: isSelected
                          ? 'sepia(1) saturate(8) hue-rotate(60deg) brightness(0.95)'
                          : 'none',
                      }}
                    />
                  </button>
                )
              })}
            </div>
            <small style={{ color: '#888' }}>
              {players.length} pelaajaa |{' '}
              <span style={{ color: '#2ecc71' }}>✓ {validCount}</span> |{' '}
              <span style={{ color: '#e74c3c' }}>✗ {invalidCount}</span>
            </small>
          </div>
          {/* Oikean puolen kontrollit näytetään vain ei-niputetuille tai ylimmälle */}
          {!isBundled && (
            <div
              className="enhanced-list-header-right"
              style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
            >
              <Form.Check
                type="switch"
                id="filter-invalid"
                label="Vain virheelliset"
                checked={filterOnlyInvalid}
                onChange={e => setFilterOnlyInvalid(e.target.checked)}
                style={{ color: '#FFFFFF' }}
              />
              <Form.Check
                type="switch"
                id="manage-mode"
                label="Hallinnoi listaa"
                checked={manageMode}
                onChange={e => {
                  setManageMode(e.target.checked)
                  setConfirmDelete(null)
                }}
                style={{ color: '#FFFFFF' }}
              />
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => setShowStandardsModal(true)}
              >
                ⚙️ Asetukset
              </Button>
              <Button
                variant="info"
                size="sm"
                onClick={() => setShowAddModal(true)}
              >
                + Lisää
              </Button>
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => {
                  setCopySourceListId(null)
                  setShowCopyModal(true)
                }}
              >
                Kopioi lista
              </Button>
              {manageMode && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    setRemoveSourceListId(null)
                    setShowRemovePaintModal(true)
                  }}
                >
                  Poista pelaajia
                </Button>
              )}
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => setShowCsvSyncModal(true)}
              >
                CSV
              </Button>
              <Button
                variant={
                  isFavoriteCandidateList(candidate?.id || candidate?._id || id)
                    ? 'info'
                    : 'outline-info'
                }
                size="sm"
                style={{ minWidth: '30px', padding: '0.1rem 0.3rem' }}
                aria-label="Suosikki"
                title="Suosikki"
                onClick={() =>
                  toggleFavoriteCandidateList(candidate?.id || candidate?._id || id)
                }
              >
                {isFavoriteCandidateList(candidate?.id || candidate?._id || id)
                  ? '\u2605'
                  : '\u2606'}
              </Button>
            </div>
          )}
          {manageMode && !isBundled && (
            <div
              style={{
                width: '100%',
                marginTop: '8px',
                padding: '8px 10px',
                border: '1px solid #3A3F44',
                borderRadius: '6px',
                backgroundColor: '#1E2124',
              }}
            >
              <div
                style={{
                  marginBottom: '6px',
                  fontSize: '0.8rem',
                  lineHeight: 1.1,
                }}
              >
                Piilotetut sarakkeet ({candidate?.Position || 'yleinen'})
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
                  gap: '4px 8px',
                }}
              >
                {MANAGEABLE_COLUMNS.map(column => {
                  const hiddenColumns = getHiddenColumnsForCandidate(candidate)
                  const checked = !hiddenColumns.has(column.key)

                  return (
                    <Form.Check
                      key={column.key}
                      type="checkbox"
                      label={column.label}
                      checked={checked}
                      onChange={() =>
                        toggleColumnVisibility(candidate, column.key)
                      }
                      style={{
                        color: '#FFFFFF',
                        fontSize: '0.72rem',
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        marginBottom: 0,
                      }}
                    />
                  )
                })}
              </div>
              <div
                style={{
                  marginTop: '12px',
                  paddingTop: '8px',
                  borderTop: '1px solid #3A3F44',
                }}
              >
                <div
                  style={{
                    marginBottom: '6px',
                    fontSize: '0.8rem',
                    lineHeight: 1.1,
                  }}
                >
                  HTMS({getCandidatePosition(candidate) || '?'})-pelipaikan
                  taidot
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px 12px',
                  }}
                >
                  {[
                    ['keeperSkill', 'MV'],
                    ['defenderSkill', 'PU'],
                    ['playmakerSkill', 'PR'],
                    ['wingerSkill', 'LA'],
                    ['passingSkill', 'SY'],
                    ['scorerSkill', 'MT'],
                    ['setPiecesSkill', 'ET'],
                  ].map(([skill, label]) => (
                    <Form.Check
                      key={skill}
                      type="checkbox"
                      label={label}
                      checked={getPositionHtmsSkills(candidate).includes(skill)}
                      onChange={() => handlePositionHtmsSkillsChange(skill)}
                      style={{
                        color: '#FFFFFF',
                        fontSize: '0.72rem',
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        marginBottom: 0,
                      }}
                    />
                  ))}
                </div>
                {getCandidatePosition(candidate) === 'GK' && (
                  <small style={{ color: '#888' }}>
                    Arvaamattomalle maalivahdille syöttäminen lasketaan aina
                    mukaan.
                  </small>
                )}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Niputettu näkymä: useampi lista peräkkäin */}
      {isBundled && bundledCandidates.length > 0 ? (
        bundledCandidates.map(bundleEntry => {
          const bc = bundleEntry.candidate
          const bcId = bc.id || bc._id
          const bcPlayers = bundleEntry.players
          const bcStandards = bundleEntry.trainingStandards
          const bcChecks = bundleEntry.trainingChecks || {}
          const competitionSeasonText =
            bc.Competition && bc.Season
              ? `${bc.Competition} ${bc.Season}`
              : bc.Competition || bc.Season || ''

          // Filtteröi ja sorttaa tämän listan pelaajat
          const checkPlayerValidityForStandards = (entry, standards) => {
            const player = entry.player
            if (!player) return false
            const defaultStandards = {
              coach: { target: 5, tolerance: 0 },
              assistants: { target: 10, tolerance: 0 },
              intensity: { target: 100, tolerance: 0 },
              stamina: { target: 10, tolerance: 0 },
              minutes: { target: 90, tolerance: 0 },
              efficiency: { target: 1, tolerance: 0 },
              medic: { target: 5, tolerance: 0 },
              formCoach: { target: 0, tolerance: 0 },
            }
            const stds = { ...defaultStandards, ...(standards || {}) }
            const data = player.trainingData || {}
            const checkOne = (value, key) => {
              const std = stds[key]
              if (!std || value === undefined || value === null) return true
              return Math.abs(value - std.target) <= std.tolerance
            }
            return (
              checkOne(data.coach, 'coach') &&
              checkOne(data.assistants, 'assistants') &&
              checkOne(data.intensitety, 'intensity') &&
              checkOne(data.stamina, 'stamina') &&
              checkOne(data.minutes, 'minutes') &&
              checkOne(data.effency, 'efficiency') &&
              checkOne(data.medic, 'medic') &&
              checkOne(data.formCoach, 'formCoach')
            )
          }

          let displayBcPlayers = getValidPlayerEntries(bcPlayers)
          const minAge = minAgeYears === '' ? null : Number(minAgeYears)
          const maxAge = maxAgeYears === '' ? null : Number(maxAgeYears)

          if (Number.isFinite(minAge)) {
            displayBcPlayers = displayBcPlayers.filter(
              entry => calculateAge(entry).years >= minAge
            )
          }

          if (Number.isFinite(maxAge)) {
            displayBcPlayers = displayBcPlayers.filter(
              entry => calculateAge(entry).years <= maxAge
            )
          }

          if (selectedSpecialties.length > 0) {
            displayBcPlayers = displayBcPlayers.filter(entry => {
              const specialty = Number(entry?.player?.specialty)
              return (
                Number.isFinite(specialty) &&
                selectedSpecialties.includes(specialty)
              )
            })
          }

          if (!manageMode) {
            displayBcPlayers = displayBcPlayers.filter(entry => !entry.hidden)
          }
          if (filterOnlyInvalid) {
            displayBcPlayers = displayBcPlayers.filter(
              entry => !checkPlayerValidityForStandards(entry, bcStandards)
            )
          }

          const bcValidCount = bcPlayers.filter(p =>
            checkPlayerValidityForStandards(p, bcStandards)
          ).length
          const bcInvalidCount = bcPlayers.length - bcValidCount

          return (
            <div key={bcId} className="mb-4">
              {/* Listan otsikko */}
              <Card
                className="mb-2"
                style={{
                  backgroundColor: '#2C2F33',
                  color: '#FFFFFF',
                  borderColor: '#444',
                }}
              >
                <Card.Body
                  className="enhanced-list-header-body"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '10px',
                    padding: '8px 15px',
                  }}
                >
                  <div
                    className="enhanced-list-header-left"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <strong>{formatCandidateName(bc)}</strong>
                    {competitionSeasonText && (
                      <span style={{ color: '#888' }}>
                        {competitionSeasonText}
                      </span>
                    )}
                    <small style={{ color: '#888' }}>
                      {bcPlayers.length} pelaajaa |{' '}
                      <span style={{ color: '#2ecc71' }}>✓ {bcValidCount}</span>{' '}
                      |{' '}
                      <span style={{ color: '#e74c3c' }}>
                        ✗ {bcInvalidCount}
                      </span>
                    </small>
                  </div>
                  <div
                    className="enhanced-list-header-right"
                    style={{
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'center',
                    }}
                  >
                    <Form.Check
                      type="switch"
                      id={`filter-invalid-${bcId}`}
                      label="Vain virheelliset"
                      checked={filterOnlyInvalid}
                      onChange={e => setFilterOnlyInvalid(e.target.checked)}
                      style={{ color: '#FFFFFF' }}
                    />
                    <Form.Check
                      type="switch"
                      id={`manage-mode-${bcId}`}
                      label="Hallinnoi listaa"
                      checked={manageMode}
                      onChange={e => {
                        setManageMode(e.target.checked)
                        setConfirmDelete(null)
                      }}
                      style={{ color: '#FFFFFF' }}
                    />
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={() => {
                        setActiveBundleListId(bcId)
                        setShowStandardsModal(true)
                      }}
                    >
                      ⚙️ Asetukset
                    </Button>
                    <Button
                      variant="info"
                      size="sm"
                      onClick={() => {
                        setActiveBundleListId(bcId)
                        setShowAddModal(true)
                      }}
                    >
                      + Lisää
                    </Button>
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => {
                        setCopySourceListId(bcId)
                        setShowCopyModal(true)
                      }}
                    >
                      Kopioi lista
                    </Button>
                    {manageMode && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setRemoveSourceListId(bcId)
                          setShowRemovePaintModal(true)
                        }}
                      >
                        Poista pelaajia
                      </Button>
                    )}
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => {
                        setActiveBundleListId(bcId)
                        setShowCsvSyncModal(true)
                      }}
                    >
                      CSV
                    </Button>
                    <Button
                      variant={
                        isFavoriteCandidateList(bcId) ? 'info' : 'outline-info'
                      }
                      size="sm"
                      style={{ minWidth: '30px', padding: '0.1rem 0.3rem' }}
                      aria-label="Suosikki"
                      title="Suosikki"
                      onClick={() => toggleFavoriteCandidateList(bcId)}
                    >
                      {isFavoriteCandidateList(bcId) ? '\u2605' : '\u2606'}
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {/* Pelaajataulukko */}
              {isLoadingPlayers ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spinner animation="border" variant="light" size="sm" />
                </div>
              ) : displayBcPlayers.length > 0 ? (
                <div
                  className="enhanced-list-table-wrap"
                  style={{ overflowX: 'auto' }}
                >
                  <Table
                    className="enhanced-list-table"
                    striped
                    bordered
                    hover
                    variant="dark"
                    size="sm"
                    style={{ fontSize: '0.85rem', textAlign: 'center' }}
                  >
                    <thead>
                      {(() => {
                        const hiddenColumns = getHiddenColumnsForCandidate(bc)
                        const showTsiDirection =
                          !hiddenColumns.has('tsi') &&
                          displayBcPlayers.some(entry =>
                            getEstimatedFormDirection(entry.player)
                          )

                        return (
                          <tr>
                            {!hiddenColumns.has('name') && (
                              <SortableHeader
                                field="name"
                                label="Nimi"
                                align="left"
                              />
                            )}
                            {!hiddenColumns.has('playerId') && (
                              <th style={{ textAlign: 'left' }}>ID</th>
                            )}
                            {!hiddenColumns.has('age') && (
                              <SortableHeader field="age" label="Ikä" />
                            )}
                            {getTournamentFromCandidate(bc) &&
                              !hiddenColumns.has('tournament') && (
                                <th>{getTournamentFromCandidate(bc).type}</th>
                              )}
                            {!hiddenColumns.has('specialty') && (
                              <SortableHeader field="specialty" label="Erik." />
                            )}
                            {!hiddenColumns.has('playerForm') && (
                              <SortableHeader
                                field="playerForm"
                                label="KU"
                                tooltip="Kunto"
                              />
                            )}
                            {showTsiDirection && (
                              <SortableHeader
                                field="tsi"
                                label="KS"
                                tooltip="Kunnon suunta"
                              />
                            )}
                            {!hiddenColumns.has('staminaSkill') && (
                              <SortableHeader
                                field="staminaSkill"
                                label="Kest."
                                tooltip="Kestävyystaito"
                              />
                            )}
                            {!hiddenColumns.has('keeperSkill') && (
                              <SortableHeader field="keeperSkill" label="MV" />
                            )}
                            {!hiddenColumns.has('defenderSkill') && (
                              <SortableHeader
                                field="defenderSkill"
                                label="PU"
                              />
                            )}
                            {!hiddenColumns.has('playmakerSkill') && (
                              <SortableHeader
                                field="playmakerSkill"
                                label="PR"
                              />
                            )}
                            {!hiddenColumns.has('wingerSkill') && (
                              <SortableHeader field="wingerSkill" label="LA" />
                            )}
                            {!hiddenColumns.has('passingSkill') && (
                              <SortableHeader field="passingSkill" label="SY" />
                            )}
                            {!hiddenColumns.has('scorerSkill') && (
                              <SortableHeader field="scorerSkill" label="MT" />
                            )}
                            {!hiddenColumns.has('setPiecesSkill') && (
                              <SortableHeader
                                field="setPiecesSkill"
                                label="ET"
                              />
                            )}
                            {!hiddenColumns.has('experience') && (
                              <SortableHeader field="experience" label="Kok" />
                            )}
                            {!hiddenColumns.has('leadership') && (
                              <SortableHeader
                                field="leadership"
                                label="Joht."
                              />
                            )}
                            {!hiddenColumns.has('htms') && (
                              <SortableHeader field="htms" label="HTMS" />
                            )}
                            {!hiddenColumns.has('positionHtms') && (
                              <SortableHeader
                                field="positionHtms"
                                label={`HTMS(${getCandidatePosition(bc) || '?'})`}
                              />
                            )}
                            {!hiddenColumns.has('potHtms') && (
                              <SortableHeader field="potHtms" label="Pot28" />
                            )}
                            {!hiddenColumns.has('coach') && (
                              <SortableHeader
                                field="coach"
                                label="V"
                                tooltip="Valmentaja"
                              />
                            )}
                            {!hiddenColumns.has('assistants') && (
                              <SortableHeader
                                field="assistants"
                                label="AV"
                                tooltip="Apuvalmentajat"
                              />
                            )}
                            {!hiddenColumns.has('intensity') && (
                              <SortableHeader field="intensity" label="Int." />
                            )}
                            {!hiddenColumns.has('staminaTraining') && (
                              <SortableHeader field="stamina" label="Kest." />
                            )}
                            {!hiddenColumns.has('minutes') && <th>Mins</th>}
                            {!hiddenColumns.has('efficiency') && (
                              <th title="Teho">Ø</th>
                            )}
                            {!hiddenColumns.has('medic') && (
                              <SortableHeader
                                field="medic"
                                label="L"
                                tooltip="Lääkäri"
                              />
                            )}
                            {!hiddenColumns.has('formCoach') && (
                              <SortableHeader
                                field="formCoach"
                                label="KV"
                                tooltip="Kuntovalkku"
                              />
                            )}
                            {!hiddenColumns.has('trainingMode') && (
                              <th>Harj.</th>
                            )}
                            {!hiddenColumns.has('trainingPlan') && (
                              <th>Plan</th>
                            )}
                            {!hiddenColumns.has('owner') && (
                              <SortableHeader
                                field="owner"
                                label="Omistaja"
                                align="left"
                              />
                            )}
                            {manageMode &&
                              !hiddenColumns.has('manageVisibility') && (
                                <th></th>
                              )}
                            {manageMode &&
                              !hiddenColumns.has('manageDelete') && <th></th>}
                          </tr>
                        )
                      })()}
                    </thead>
                    <tbody>
                      {(() => {
                        const hiddenColumns = getHiddenColumnsForCandidate(bc)
                        const showTsiDirection =
                          !hiddenColumns.has('tsi') &&
                          displayBcPlayers.some(entry =>
                            getEstimatedFormDirection(entry.player)
                          )
                        return sortPlayers(displayBcPlayers, bc).map(entry =>
                          renderPlayerRow(
                            entry,
                            bcStandards,
                            bcChecks,
                            bcId,
                            bc,
                            hiddenColumns,
                            showTsiDirection
                          )
                        )
                      })()}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Card
                  style={{
                    backgroundColor: '#2C2F33',
                    color: '#888',
                    textAlign: 'center',
                    padding: '20px',
                  }}
                >
                  <Card.Body>
                    {filterOnlyInvalid
                      ? 'Ei pelaajia virheellisellä statuksella.'
                      : 'Listalla ei ole vielä pelaajia.'}
                  </Card.Body>
                </Card>
              )}
            </div>
          )
        })
      ) : (
        /* Normaali yksittäisen listan näkymä */
        <>
          {/* Pelaajataulukko */}
          {isLoadingPlayers ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spinner animation="border" variant="light" />
              <p style={{ color: '#888', marginTop: '10px' }}>
                Ladataan pelaajia...
              </p>
            </div>
          ) : displayPlayers.length > 0 ? (
            <div
              className="enhanced-list-table-wrap"
              style={{ overflowX: 'auto' }}
            >
              <Table
                className="enhanced-list-table"
                striped
                bordered
                hover
                variant="dark"
                size="sm"
                style={{ fontSize: '0.85rem', textAlign: 'center' }}
              >
                <thead>
                  {(() => {
                    const hiddenColumns =
                      getHiddenColumnsForCandidate(candidate)
                    const showTsiDirection =
                      !hiddenColumns.has('tsi') &&
                      displayPlayers.some(entry =>
                        getEstimatedFormDirection(entry.player)
                      )

                    return (
                      <tr>
                        {!hiddenColumns.has('name') && (
                          <SortableHeader
                            field="name"
                            label="Nimi"
                            align="left"
                          />
                        )}
                        {!hiddenColumns.has('playerId') && (
                          <th style={{ textAlign: 'left' }}>ID</th>
                        )}
                        {!hiddenColumns.has('age') && (
                          <SortableHeader field="age" label="Ikä" />
                        )}
                        {getTournamentFromCandidate(candidate) &&
                          !hiddenColumns.has('tournament') && (
                            <th>
                              {getTournamentFromCandidate(candidate).type}
                            </th>
                          )}
                        {!hiddenColumns.has('specialty') && (
                          <SortableHeader field="specialty" label="Erik." />
                        )}
                        {!hiddenColumns.has('playerForm') && (
                          <SortableHeader
                            field="playerForm"
                            label="KU"
                            tooltip="Kunto"
                          />
                        )}
                        {showTsiDirection && (
                          <SortableHeader
                            field="tsi"
                            label="KS"
                            tooltip="Kunnon suunta"
                          />
                        )}
                        {!hiddenColumns.has('staminaSkill') && (
                          <SortableHeader
                            field="staminaSkill"
                            label="Kest."
                            tooltip="Kestävyystaito"
                          />
                        )}
                        {!hiddenColumns.has('keeperSkill') && (
                          <SortableHeader field="keeperSkill" label="MV" />
                        )}
                        {!hiddenColumns.has('defenderSkill') && (
                          <SortableHeader field="defenderSkill" label="PU" />
                        )}
                        {!hiddenColumns.has('playmakerSkill') && (
                          <SortableHeader field="playmakerSkill" label="PR" />
                        )}
                        {!hiddenColumns.has('wingerSkill') && (
                          <SortableHeader field="wingerSkill" label="LA" />
                        )}
                        {!hiddenColumns.has('passingSkill') && (
                          <SortableHeader field="passingSkill" label="SY" />
                        )}
                        {!hiddenColumns.has('scorerSkill') && (
                          <SortableHeader field="scorerSkill" label="MT" />
                        )}
                        {!hiddenColumns.has('setPiecesSkill') && (
                          <SortableHeader field="setPiecesSkill" label="ET" />
                        )}
                        {!hiddenColumns.has('experience') && (
                          <SortableHeader field="experience" label="Kok" />
                        )}
                        {!hiddenColumns.has('leadership') && (
                          <SortableHeader field="leadership" label="Joht." />
                        )}
                        {!hiddenColumns.has('htms') && (
                          <SortableHeader field="htms" label="HTMS" />
                        )}
                        {!hiddenColumns.has('positionHtms') && (
                          <SortableHeader
                            field="positionHtms"
                            label={`HTMS(${getCandidatePosition(candidate) || '?'})`}
                          />
                        )}
                        {!hiddenColumns.has('potHtms') && (
                          <SortableHeader field="potHtms" label="Pot28" />
                        )}
                        {!hiddenColumns.has('coach') && (
                          <SortableHeader
                            field="coach"
                            label="V"
                            tooltip="Valmentaja"
                          />
                        )}
                        {!hiddenColumns.has('assistants') && (
                          <SortableHeader
                            field="assistants"
                            label="AV"
                            tooltip="Apuvalmentajat"
                          />
                        )}
                        {!hiddenColumns.has('intensity') && (
                          <SortableHeader field="intensity" label="Int." />
                        )}
                        {!hiddenColumns.has('staminaTraining') && (
                          <SortableHeader field="stamina" label="Kest." />
                        )}
                        {!hiddenColumns.has('minutes') && <th>Mins</th>}
                        {!hiddenColumns.has('efficiency') && (
                          <th title="Teho">Ø</th>
                        )}
                        {!hiddenColumns.has('medic') && (
                          <SortableHeader
                            field="medic"
                            label="L"
                            tooltip="Lääkäri"
                          />
                        )}
                        {!hiddenColumns.has('formCoach') && (
                          <SortableHeader
                            field="formCoach"
                            label="KV"
                            tooltip="Kuntovalkku"
                          />
                        )}
                        {!hiddenColumns.has('trainingMode') && <th>Harj.</th>}
                        {!hiddenColumns.has('trainingPlan') && <th>Plan</th>}
                        {!hiddenColumns.has('owner') && (
                          <SortableHeader
                            field="owner"
                            label="Omistaja"
                            align="left"
                          />
                        )}
                        {manageMode &&
                          !hiddenColumns.has('manageVisibility') && <th></th>}
                        {manageMode && !hiddenColumns.has('manageDelete') && (
                          <th></th>
                        )}
                      </tr>
                    )
                  })()}
                </thead>
                <tbody>
                  {(() => {
                    const hiddenColumns =
                      getHiddenColumnsForCandidate(candidate)
                    const showTsiDirection =
                      !hiddenColumns.has('tsi') &&
                      displayPlayers.some(entry =>
                        getEstimatedFormDirection(entry.player)
                      )
                    return displayPlayers.map(entry =>
                      renderPlayerRow(
                        entry,
                        trainingStandards,
                        trainingChecks,
                        id,
                        candidate,
                        hiddenColumns,
                        showTsiDirection
                      )
                    )
                  })()}
                </tbody>
              </Table>
            </div>
          ) : (
            <Card
              style={{
                backgroundColor: '#2C2F33',
                color: '#888',
                textAlign: 'center',
                padding: '40px',
              }}
            >
              <Card.Body>
                {filterOnlyInvalid
                  ? 'Ei pelaajia virheellisellä statuksella.'
                  : 'Listalla ei ole vielä pelaajia. Lisää pelaajia "Lisää" -painikkeella.'}
              </Card.Body>
            </Card>
          )}
        </>
      )}

      {/* Modalit */}
      <AddPlayersByIdModal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false)
          setActiveBundleListId(null)
        }}
        onAddPlayers={
          activeBundleListId ? handleAddPlayersToBundle : handleAddPlayers
        }
        existingPlayerIds={
          activeBundleListId
            ? (
                bundledCandidates.find(
                  bc =>
                    (bc.candidate.id || bc.candidate._id) === activeBundleListId
                )?.players || []
              ).map(entry => String(entry.player.playerId))
            : players.map(entry => String(entry.player.playerId))
        }
      />

      <CandidateCsvSyncModal
        show={showCsvSyncModal}
        onHide={() => {
          setShowCsvSyncModal(false)
          setActiveBundleListId(null)
        }}
        candidateId={activeBundleListId || id}
        onSynced={handleCsvSynced}
      />

      <CopyCandidateListModal
        show={showCopyModal}
        onHide={() => {
          setShowCopyModal(false)
          setCopySourceListId(null)
        }}
        sourceCandidate={
          copySourceListId
            ? bundledCandidates.find(
                bc => (bc.candidate.id || bc.candidate._id) === copySourceListId
              )?.candidate || null
            : candidate
        }
        sourceEntries={
          copySourceListId
            ? bundledCandidates.find(
                bc => (bc.candidate.id || bc.candidate._id) === copySourceListId
              )?.players || []
            : players
        }
        onCopy={handleCopyList}
      />

      <RemovePlayersPaintModal
        show={showRemovePaintModal}
        onHide={() => {
          setShowRemovePaintModal(false)
          setRemoveSourceListId(null)
        }}
        sourceCandidate={
          removeSourceListId
            ? bundledCandidates.find(
                bc =>
                  (bc.candidate.id || bc.candidate._id) === removeSourceListId
              )?.candidate || null
            : candidate
        }
        sourceEntries={
          removeSourceListId
            ? bundledCandidates.find(
                bc =>
                  (bc.candidate.id || bc.candidate._id) === removeSourceListId
              )?.players || []
            : players
        }
        onRemove={handleRemovePlayersByPaint}
      />

      <TrainingStandardsModal
        show={showStandardsModal}
        onHide={() => {
          setShowStandardsModal(false)
          setActiveBundleListId(null)
        }}
        currentStandards={
          activeBundleListId
            ? bundledCandidates.find(
                bc =>
                  (bc.candidate.id || bc.candidate._id) === activeBundleListId
              )?.trainingStandards || null
            : trainingStandards
        }
        currentName={
          activeBundleListId
            ? bundledCandidates.find(
                bc =>
                  (bc.candidate.id || bc.candidate._id) === activeBundleListId
              )?.candidate.Name || ''
            : candidate
            ? candidate.Name
            : ''
        }
        onSaveStandards={
          activeBundleListId
            ? (standards, newName) =>
                handleSaveStandardsForBundle(
                  activeBundleListId,
                  standards,
                  newName
                )
            : handleSaveStandards
        }
        onTypeSeasonSaved={applyTypeSeasonUpdate}
        candidate={
          activeBundleListId
            ? bundledCandidates.find(
                bc =>
                  (bc.candidate.id || bc.candidate._id) === activeBundleListId
              )?.candidate || null
            : candidate
        }
        id={activeBundleListId || id}
      />
    </Container>
  )
}

export default EnhancedCandidatesList
